import {
  createTheme,
  type PaletteMode,
  type Theme,
  THEME_ID,
} from '@mui/material/styles';
import { useThemeWithoutDefault } from '@mui/system';

import {
  createThemeSettings,
  FALLBACK_DEFAULT_PARKDNA_DARK_THEME_PALETTE,
  FALLBACK_PARKDNA_DEFAULT_LIGHT_THEME_PALETTE,
  type ThemePalettes,
  themePaletteSchema,
} from './theme-constants';
import { createThemeSettingsV2, type ThemeVersion } from './theme-v2';
import { useThemeNameStore } from './themeStore';

export const themeSettings = (mode: PaletteMode) => {
  const themePalettes = getThemePalettesFromEnv();
  return createThemeSettings(mode, themePalettes);
};

export type { ThemeVersion } from './theme-v2';

const themeByKey = new Map<`${PaletteMode}:${ThemeVersion}`, Theme>();

// One Theme instance per mode and version for the app lifetime. createTheme is
// expensive and the palette comes from build-time env, so there is nothing to
// rebuild. v1 is the default and is unchanged by the version argument.
export const createAppTheme = (
  mode: PaletteMode,
  version: ThemeVersion = 'v1'
): Theme => {
  const key = `${mode}:${version}` as const;
  const cached = themeByKey.get(key);
  if (cached) {
    return cached;
  }
  const theme = createTheme(
    version === 'v2'
      ? createThemeSettingsV2(mode, getThemePalettesFromEnv())
      : themeSettings(mode)
  );
  themeByKey.set(key, theme);
  return theme;
};

// A Joy theme also carries palette and breakpoints; mixins and transitions
// exist only on a Material theme.
const isMaterialTheme = (value: unknown): value is Theme =>
  typeof value === 'object' &&
  value !== null &&
  'palette' in value &&
  'mixins' in value &&
  'transitions' in value;

// The emotion context is scoped by library id, so under a nested Joy provider
// it may hold only { $$joy } and outside any provider it is null.
const readMaterialTheme = (contextTheme: object | null): Theme | null => {
  if (!contextTheme) {
    return null;
  }
  if (THEME_ID in contextTheme && isMaterialTheme(contextTheme[THEME_ID])) {
    return contextTheme[THEME_ID];
  }
  return isMaterialTheme(contextTheme) ? contextTheme : null;
};

export const useTheme = (): readonly [Theme, VoidFunction] => {
  const contextTheme = useThemeWithoutDefault<object>();
  const themeName = useThemeNameStore((state) => state.themeName);
  const toggleTheme = useThemeNameStore((state) => state.toggleTheme);

  const theme = readMaterialTheme(contextTheme) ?? createAppTheme(themeName);

  return [theme, toggleTheme];
};

const getThemePalettesFromEnv = (): ThemePalettes => {
  return {
    light: import.meta.env.VITE_THEME_PALETTE_LIGHT_JSON
      ? themePaletteSchema.parse(
          JSON.parse(import.meta.env.VITE_THEME_PALETTE_LIGHT_JSON)
        )
      : FALLBACK_PARKDNA_DEFAULT_LIGHT_THEME_PALETTE,
    dark: import.meta.env.VITE_THEME_PALETTE_DARK_JSON
      ? themePaletteSchema.parse(
          JSON.parse(import.meta.env.VITE_THEME_PALETTE_DARK_JSON)
        )
      : FALLBACK_DEFAULT_PARKDNA_DARK_THEME_PALETTE,
  };
};
